import { memo, useState, useMemo, useCallback, useRef } from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';
import type { PieLabelProps } from 'recharts/types/polar/Pie';

import { useFormFactor } from '../../../utils/formFactor';

import { IWheelProps } from './types';

/**
 * Wrapper component that selects an optimized layout by form factor:
 * - desktop
 * - mobile-portrait
 * - mobile-landscape
 */
export const WheelOfBalance = (props: IWheelProps & { fullscreen?: boolean; disableHover?: boolean }) => {
  const formFactor = useFormFactor();

  if (formFactor === 'mobile-portrait') {
    return <MobilePortraitWheel {...props} fullscreen={props.fullscreen} disableHover={props.disableHover} />;
  }
  if (formFactor === 'mobile-landscape') {
    return <MobileLandscapeWheel {...props} fullscreen={props.fullscreen} disableHover={props.disableHover} />;
  }
  return <DesktopWheel {...props} fullscreen={props.fullscreen} disableHover={props.disableHover} />;
};

// =========================
// Shared utilities & hooks
// =========================
const colors = {
  rings: '#d3d3d3',
  spokes: '#808080',
  label: '#111',
};

const RAD = Math.PI / 180;
const SECTORS = new Array(8).fill(0).map((_, i) => ({ name: String.fromCharCode(65 + i), value: 10 }));
const RINGS = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100];

function getContrastColorForHex(hex: string) {
  const c = hex.substring(1);
  const rgb = parseInt(c, 16);
  const r = (rgb >> 16) & 0xff;
  const g = (rgb >> 8) & 0xff;
  const b = rgb & 0xff;
  const luma = 0.299 * r + 0.587 * g + 0.114 * b;
  return luma > 186 ? '#000' : '#fff';
}


// =========================
// Shared chart primitives
// =========================
function useWheelState(values: number[], saveResult?: (v: number[]) => void) {
  const sectors = SECTORS;
  const rr = RINGS;

  const [hoveredSector, setHoveredSector] = useState<number | null>(null);
  const [hoveredLevel, setHoveredLevel] = useState<number | null>(null);

  const hoverOuter = useMemo(() => (hoveredLevel ? `${hoveredLevel * 10}%` : null), [hoveredLevel]);

  const changeValue = useCallback((index: number, value: number) => {
    if (!saveResult || values[index] === value) return;
    const next = [...values];
    next[index] = value;
    saveResult(next);
  }, [saveResult, values]);

  return {
    sectors,
    rr,
    hoveredSector,
    setHoveredSector,
    hoveredLevel,
    setHoveredLevel,
    hoverOuter,
    changeValue,
  } as const;
}

const Rings = memo(function Rings({ rr }: { rr: number[] }) {
  return (
    <>
      {rr.map((r, i) => (
        <Pie
          key={`ring-${i}`}
          data={[{ name: 'ring', value: 8 }]}
          isAnimationActive={false}
          startAngle={90}
          endAngle={-270}
          innerRadius={i === 0 ? '0%' : `${r - 10}%`}
          outerRadius={`${r}%`}
          paddingAngle={0}
          dataKey="value"
          fill="transparent"
          stroke={colors.rings}
          strokeWidth={1}
        />
      ))}
      {new Array(8).fill(0).map((_, i) => (
        <Pie
          key={`spoke-${i}`}
          data={[{ name: 'sp', value: 1 }]}
          isAnimationActive={false}
          startAngle={90 - i * 45}
          endAngle={90 - (i + 1) * 45}
          innerRadius={'0%'}
          outerRadius={'100%'}
          paddingAngle={0}
          dataKey="value"
          fill="transparent"
          stroke={colors.spokes}
          strokeWidth={1}
        />
      ))}
    </>
  );
});

const HoverPreview = memo(function HoverPreview({
  hoveredSector,
  hoverOuter,
  lifeItems,
}: {
  hoveredSector: number | null;
  hoverOuter: string | null;
  lifeItems: IWheelProps['lifeItems'];
}) {
  return hoveredSector !== null && hoverOuter ? (
    <Pie
      data={[{ name: 'hover', value: 1 }]}
      isAnimationActive={false}
      animationDuration={0}
      animationEasing="linear"
      startAngle={90 - hoveredSector * 45}
      endAngle={90 - (hoveredSector + 1) * 45}
      innerRadius={'0%'}
      outerRadius={hoverOuter}
      paddingAngle={0}
      dataKey="value"
      stroke={lifeItems[hoveredSector].color}
      strokeWidth={2}
      fill={lifeItems[hoveredSector].color}
      fillOpacity={0.25}
    />
  ) : null;
});

function InteractionRings({
  sectors,
  onEnter,
  onLeave,
  onClick,
}: {
  sectors: { name: string; value: number }[];
  onEnter: (idx: number, level: number) => void;
  onLeave: () => void;
  onClick?: (idx: number, level: number) => void;
}) {
  const activePointerIdRef = useRef<number | null>(null);

  const getLevelFromEvent = (e: any): number | null => {
    const target = e?.currentTarget as SVGElement | undefined;
    const svg = target?.ownerSVGElement;
    if (!svg) return null;

    const rect = svg.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = (e?.clientX ?? 0) - cx;
    const dy = (e?.clientY ?? 0) - cy;
    const distance = Math.sqrt(dx * dx + dy * dy);
    const maxRadius = Math.min(rect.width, rect.height) / 2;
    if (maxRadius <= 0 || distance > maxRadius * 1.02) return null;

    const level = Math.ceil((distance / maxRadius) * 10);
    return Math.max(1, Math.min(10, level));
  };

  const applySelection = (e: any, idx: number) => {
    const level = getLevelFromEvent(e);
    if (level === null) return;
    onEnter(idx, level);
    if (onClick) onClick(idx, level);
  };

  return (
    <Pie
      data={sectors}
      isAnimationActive={false}
      startAngle={90}
      endAngle={-270}
      innerRadius={'0%'}
      outerRadius={'100%'}
      paddingAngle={0}
      dataKey="value"
      fill="transparent"
      stroke="transparent"
      labelLine={false}
    >
      {sectors.map((_, idx) => (
        <Cell
          key={`int-cell-${idx}`}
          fill="#000"
          fillOpacity={0.001}
          style={{ pointerEvents: 'all' }}
          onPointerDown={(e: any) => {
            activePointerIdRef.current = e?.pointerId ?? null;
            e?.preventDefault?.();
            (e?.currentTarget as any)?.setPointerCapture?.(e?.pointerId);
            applySelection(e, idx);
          }}
          onPointerMove={(e: any) => {
            if (activePointerIdRef.current === null) return;
            if (e?.pointerId !== activePointerIdRef.current) return;
            applySelection(e, idx);
          }}
          onPointerUp={(e: any) => {
            if (activePointerIdRef.current === e?.pointerId) {
              activePointerIdRef.current = null;
              onLeave();
            }
          }}
          onPointerCancel={(e: any) => {
            if (activePointerIdRef.current === e?.pointerId) {
              activePointerIdRef.current = null;
              onLeave();
            }
          }}
          onMouseLeave={() => {
            if (activePointerIdRef.current === null) {
              onLeave();
            }
          }}
          cursor={onClick ? 'pointer' : 'default'}
        />
      ))}
    </Pie>
  );
}

const FilledSectors = memo(function FilledSectors({ sectors, values, lifeItems }: Pick<IWheelProps, 'values' | 'lifeItems'> & { sectors: { name: string; value: number }[] }) {
  return (
    <>
      {sectors.map((_, idx) => {
        const start = 90 - idx * 45;
        const end = start - 45;
        const outer = `${values[idx] * 10}%`;
        return (
          <Pie
            key={`fill-${idx}`}
            data={[{ name: `s-${idx}`, value: 1 }]}
            isAnimationActive={false}
            startAngle={start}
            endAngle={end}
            innerRadius={'0%'}
            outerRadius={outer}
            paddingAngle={0}
            dataKey="value"
            stroke="none"
            fill={lifeItems[idx].color}
            fillOpacity={0.7}
          />
        );
      })}
    </>
  );
});

function makeOuterLabelRenderer({
  lifeItems,
  values,
  preferShortTitle,
  avgChar,
  maxTextW,
}: {
  lifeItems: IWheelProps['lifeItems'];
  values: IWheelProps['values'];
  preferShortTitle?: boolean;
  avgChar: number;
  maxTextW: number;
}) {
  return (idx: number) => (props: PieLabelProps) => {
    const { cx = 0, cy = 0, midAngle = 0, outerRadius = 0 } = props as Required<PieLabelProps>;
    const edgeGap = 0;
    const badgeD = 34;
    const badgeR = badgeD / 2;

    const r = outerRadius + edgeGap + badgeR;
    const anchorX = Math.round(cx + r * Math.cos(midAngle * RAD));
    const anchorY = Math.round(cy - r * Math.sin(midAngle * RAD));

    const isShortTitle = preferShortTitle && !!lifeItems[idx].shortTitle;
    const text = isShortTitle ? (lifeItems[idx].shortTitle as string) : lifeItems[idx].title;

    const innerGap = 6;
    const rawTextW = Math.ceil(text.length * avgChar);
    const textW = Math.min(rawTextW, maxTextW);

    const boxW = badgeD + innerGap + textW;
    const boxH = 44;
    const alignRightSide = anchorX > cx;

    const boxX = alignRightSide ? anchorX - badgeR : anchorX - (textW + innerGap + badgeR);
    const boxY = anchorY - boxH / 2;

    const fg = getContrastColorForHex(lifeItems[idx].color);

    return (
      <foreignObject x={boxX} y={boxY} width={boxW} height={boxH} style={{ overflow: 'visible', pointerEvents: 'none' }}>
        <div
          style={{
            fontSize: '16px',
            color: colors.label,
            wordWrap: 'break-word',
            whiteSpace: 'normal',
            lineHeight: '1.1',
            display: 'flex',
            alignItems: 'center',
            justifyContent: alignRightSide ? 'flex-start' : 'flex-end',
            gap: '6px',
            flexDirection: alignRightSide ? 'row' : 'row-reverse',
            textAlign: alignRightSide ? 'left' : 'right',
          }}
        >
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '34px',
              height: '34px',
              minWidth: '34px',
              minHeight: '34px',
              borderRadius: '50%',
              backgroundColor: lifeItems[idx].color,
              color: fg,
              fontSize: '18px',
              fontWeight: 700,
              lineHeight: '34px',
              textAlign: 'center',
              boxShadow: '0 0 0 1px rgba(0,0,0,0.1) inset',
              pointerEvents: 'none',
            }}
          >
            {values[idx] === 0 ? '' : values[idx]}
          </span>
          <span style={{ pointerEvents: 'none' }}>{text}</span>
        </div>
      </foreignObject>
    );
  };
}

const LabelRing = memo(function LabelRing({
  sectors,
  lifeItems,
  values,
  preferShortTitle = false,
  avgChar = 25,
  maxTextW = 240,
}: {
  sectors: { name: string; value: number }[];
  lifeItems: IWheelProps['lifeItems'];
  values: IWheelProps['values'];
  preferShortTitle?: boolean;
  avgChar?: number;
  maxTextW?: number;
}) {
  const renderOuterLabel = makeOuterLabelRenderer({ lifeItems, values, preferShortTitle, avgChar, maxTextW });
  return (
    <Pie
      data={sectors}
      isAnimationActive={false}
      startAngle={90}
      endAngle={-270}
      innerRadius={'102%'}
      outerRadius={'110%'}
      dataKey="value"
      fill="transparent"
      stroke="transparent"
      labelLine={false}
      label={(p: PieLabelProps) => {
        const idx = sectors.findIndex((s) => s.name === (p.name as string));
        return renderOuterLabel(idx)(p);
      }}
    >
      {sectors.map((_, i) => (
        <Cell key={`lab-${i}`} fill="transparent" />
      ))}
    </Pie>
  );
});

function MobileTitle({ idx, lifeItems, values, side = 'left' as 'left' | 'right', useShortTitle = true }) {
  // const text = lifeItems[idx].shortTitle ? (lifeItems[idx].shortTitle as string) : lifeItems[idx].title;
  var text;
  if (useShortTitle && lifeItems[idx].shortTitle) {
     text = lifeItems[idx].shortTitle;
  } else {
    text = lifeItems[idx].title;
  }
  const bg = lifeItems[idx].color;
  const fg = getContrastColorForHex(bg);
  const flexDir = side === 'right' ? 'row-reverse' : 'row';
  const align = side === 'right' ? 'right' : 'left';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexDirection: flexDir, textAlign: align }}>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '20px',
          height: '20px',
          minWidth: '20px',
          minHeight: '20px',
          borderRadius: '50%',
          backgroundColor: bg,
          color: fg,
          fontSize: '12px',
          fontWeight: 700,
          lineHeight: '24px',
          textAlign: 'center',
          boxShadow: '0 0 0 1px rgba(0,0,0,0.1) inset',
        }}
      >
        {values[idx] === 0 ? '' : values[idx]}
      </span>
      <span
       style={{
          fontSize: '14px',
        }}
      >{text}</span>
    </div>
  );
}

// =========================
// Desktop
// =========================
function DesktopWheel({ lifeItems, values, saveResult, fullscreen, disableHover }: IWheelProps & { fullscreen?: boolean; disableHover?: boolean }) {
  const { sectors, rr, hoveredSector, hoveredLevel, hoverOuter, setHoveredLevel, setHoveredSector, changeValue } =
    useWheelState(values, saveResult);

  return (
    <div className="w-full h-full">
      {fullscreen ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', width: '100%', height: '100%' }}>
         
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ResponsiveContainer width="100%" height="65%" style={{ margin: '0 auto' }}>
              <PieChart className="recharts__pie-chart">
                <Rings rr={rr} />
                {!disableHover && (<HoverPreview hoveredSector={hoveredSector} hoverOuter={hoverOuter} lifeItems={lifeItems} />)}
                <FilledSectors sectors={sectors} values={values} lifeItems={lifeItems} />
                <InteractionRings
                  sectors={sectors}
                  onEnter={disableHover ? () => {} : (idx, lvl) => { setHoveredSector(idx); setHoveredLevel(lvl) }}
                  onLeave={disableHover ? () => {} : () => { setHoveredSector(null); setHoveredLevel(null) }}
                  onClick={saveResult ? (idx, lvl) => changeValue(idx, lvl) : undefined}
                />
                <LabelRing sectors={sectors} lifeItems={lifeItems} values={values} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      ) : (
        <div className="w-full h-full justify-items-center">
          <ResponsiveContainer
            width="50%"
            height="90%"
            style={{ padding: '3.5rem', margin: '0 auto' }}
          >
            <PieChart className="recharts__pie-chart">
              <Rings rr={rr} />
              {!disableHover && (<HoverPreview hoveredSector={hoveredSector} hoverOuter={hoverOuter} lifeItems={lifeItems} />)}
              <FilledSectors sectors={sectors} values={values} lifeItems={lifeItems} />
              <InteractionRings
                sectors={sectors}
                onEnter={disableHover ? () => {} : (idx, lvl) => { setHoveredSector(idx); setHoveredLevel(lvl) }}
                onLeave={disableHover ? () => {} : () => { setHoveredSector(null); setHoveredLevel(null) }}
                onClick={saveResult ? (idx, lvl) => changeValue(idx, lvl) : undefined}
              />
              <LabelRing sectors={sectors} lifeItems={lifeItems} values={values} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

// =========================
// Mobile — Portrait
// =========================
function MobilePortraitWheel({ lifeItems, values, saveResult, fullscreen, disableHover }: IWheelProps & { fullscreen?: boolean; disableHover?: boolean }) {
  const { sectors, rr, hoveredSector, hoveredLevel, hoverOuter, setHoveredLevel, setHoveredSector, changeValue } =
    useWheelState(values, saveResult);

  const upperLeftIdx = [7, 6];
  const upperRightIdx = [0, 1];
  const lowerLeftIdx = [5, 4];
  const lowerRightIdx = [2, 3];

  return (
    <div className="w-full h-full justify-items-center">
      {fullscreen ? (
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%', boxSizing: 'border-box', padding: '8px' }}>
          <div
            style={{
              flex: '1 1 50%',
              display: 'grid',
              gridTemplateColumns: '1fr',
              columnGap: '12px',
              alignItems: 'center',
              justifyItems: 'stretch',
              padding: '4px 8px',
              boxSizing: 'border-box',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {[7, 6, 5, 4, 0, 1, 2, 3].map((i) => (
                <div key={`fs-mp-left-${i}`} style={{ fontSize: '16px', lineHeight: 1.15 }}>
                  <MobileTitle idx={i} lifeItems={lifeItems} values={values} side="left" useShortTitle={false} />
                </div>
              ))}
            </div>
          </div>
          <div style={{ flex: '1 1 50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ width: '95%', height: '100%' }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart className="recharts__pie-chart">
                  <Rings rr={rr} />
                  {!disableHover && (<HoverPreview hoveredSector={hoveredSector} hoverOuter={hoverOuter} lifeItems={lifeItems} />)}
                  <FilledSectors sectors={sectors} values={values} lifeItems={lifeItems} />
                  <InteractionRings
                    sectors={sectors}
                    onEnter={disableHover ? () => {} : (idx, lvl) => { setHoveredSector(idx); setHoveredLevel(lvl) }}
                    onLeave={disableHover ? () => {} : () => { setHoveredSector(null); setHoveredLevel(null) }}
                    onClick={saveResult ? (idx, lvl) => changeValue(idx, lvl) : undefined}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      ) : (
        <div
          style={{
            width: '90vw',
            maxWidth: '100vw',
            marginLeft: 'calc(50% - 50vw)',
            marginRight: 'calc(50% - 50vw)',
            padding: '2px',
            paddingLeft: '10px',
            paddingRight: '10px',
            display: 'flex',
            flexDirection: 'column',
            boxSizing: 'border-box',
          }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: '12px', alignItems: 'start', marginBottom: '8px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {upperLeftIdx.map((i) => (
                <div key={`lt-${i}`} style={{ fontSize: '16px', lineHeight: 1.15 }}>
                  <MobileTitle idx={i} lifeItems={lifeItems} values={values} side="left" />
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px', textAlign: 'right' }}>
              {upperRightIdx.map((i) => (
                <div key={`rt-${i}`} style={{ fontSize: '16px', lineHeight: 1.15 }}>
                  <MobileTitle idx={i} lifeItems={lifeItems} values={values} side="right" />
                </div>
              ))}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: '12px', alignItems: 'start', marginTop: '2px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {lowerLeftIdx.map((i) => (
                <div key={`lb-${i}`} style={{ fontSize: '16px', lineHeight: 1.15 }}>
                  <MobileTitle idx={i} lifeItems={lifeItems} values={values} side="left" />
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px', textAlign: 'right' }}>
              {lowerRightIdx.map((i) => (
                <div key={`rb-${i}`} style={{ fontSize: '16px', lineHeight: 1.15 }}>
                  <MobileTitle idx={i} lifeItems={lifeItems} values={values} side="right" />
                </div>
              ))}
            </div>
          </div>
          <div style={{ flex: '0 0 auto', height: '40vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart className="recharts__pie-chart">
                <Rings rr={rr} />
                {!disableHover && (<HoverPreview hoveredSector={hoveredSector} hoverOuter={hoverOuter} lifeItems={lifeItems} />)}
                <FilledSectors sectors={sectors} values={values} lifeItems={lifeItems} />
                <InteractionRings
                  sectors={sectors}
                  onEnter={disableHover ? () => {} : (idx, lvl) => { setHoveredSector(idx); setHoveredLevel(lvl) }}
                  onLeave={disableHover ? () => {} : () => { setHoveredSector(null); setHoveredLevel(null) }}
                  onClick={saveResult ? (idx, lvl) => changeValue(idx, lvl) : undefined}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}

// =========================
// Mobile — Landscape 
// =========================
function MobileLandscapeWheel({ lifeItems, values, saveResult, fullscreen, disableHover }: IWheelProps & { fullscreen?: boolean; disableHover?: boolean }) {
  const { sectors, rr, hoveredSector, hoveredLevel, hoverOuter, setHoveredLevel, setHoveredSector, changeValue } =
    useWheelState(values, saveResult);

  return (
    <div className="w-full h-full justify-items-center">
      {fullscreen ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', width: '100%', height: '95%' }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr',
              columnGap: '12px',
              alignItems: 'center',
              justifyItems: 'start',
              padding: '8px',
              boxSizing: 'border-box',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {[7, 6, 5, 4, 0, 1, 2, 3].map((i) => (
                <div key={`fs-ml-left-${i}`} style={{ fontSize: 'clamp(12px, 2.2vw, 16px)', lineHeight: 1.15 }}>
                  <MobileTitle idx={i} lifeItems={lifeItems} values={values} side="left" useShortTitle={false} />
                </div>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ width: '100%', height: '100%' }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart className="recharts__pie-chart">
                  <Rings rr={rr} />
                  {!disableHover && (<HoverPreview hoveredSector={hoveredSector} hoverOuter={hoverOuter} lifeItems={lifeItems} />)}
                  <FilledSectors sectors={sectors} values={values} lifeItems={lifeItems} />
                  <InteractionRings
                    sectors={sectors}
                    onEnter={disableHover ? () => {} : (idx, lvl) => { setHoveredSector(idx); setHoveredLevel(lvl) }}
                    onLeave={disableHover ? () => {} : () => { setHoveredSector(null); setHoveredLevel(null) }}
                    onClick={saveResult ? (idx, lvl) => changeValue(idx, lvl) : undefined}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr auto 1fr',
            alignItems: 'center',
            justifyContent: 'right',
            gap: 'clamp(6px, 2vw, 16px)',
            width: '100%',
            height: '100%',
            boxSizing: 'border-box',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'clamp(1px, 0.6vw, 10px)', fontSize: 'clamp(12px, 2.2vw, 16px)', lineHeight: 1.15, maxWidth: '17vw', justifyContent: 'center' }}>
            {[7, 6, 5, 4].map((i) => (
              <div key={`land-l-${i}`}>
                <MobileTitle idx={i} lifeItems={lifeItems} values={values} side="left" />
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'clamp(1px, 0.6vw, 10px)', fontSize: 'clamp(12px, 2.2vw, 16px)', lineHeight: 1.15, maxWidth: '17vw', justifyContent: 'center' }}>
            {[0, 1, 2, 3].map((i) => (
              <div key={`land-l-${i}`}>
                <MobileTitle idx={i} lifeItems={lifeItems} values={values} side="left" />
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 'clamp(260px, 60vh, 80vw)', height: 'clamp(160px, 55vh, 82vw)', maxWidth: '30vw', maxHeight: '50vh' }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart className="recharts__pie-chart">
                <Rings rr={rr} />
                {!disableHover && (<HoverPreview hoveredSector={hoveredSector} hoverOuter={hoverOuter} lifeItems={lifeItems} />)}
                <FilledSectors sectors={sectors} values={values} lifeItems={lifeItems} />
                <InteractionRings
                  sectors={sectors}
                  onEnter={disableHover ? () => {} : (idx, lvl) => { setHoveredSector(idx); setHoveredLevel(lvl) }}
                  onLeave={disableHover ? () => {} : () => { setHoveredSector(null); setHoveredLevel(null) }}
                  onClick={saveResult ? (idx, lvl) => changeValue(idx, lvl) : undefined}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}
